import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CheckCircle, XCircle, ShieldCheck } from "@phosphor-icons/react";

export default function Verifications() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [action, setAction] = useState("approve");
  const [remarks, setRemarks] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/tasks", { params: { status: "completed" } });
    setTasks(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openTask = async (t) => {
    const { data } = await api.get(`/tasks/${t.id}`);
    setActive(data);
    setAction("approve");
    setRemarks("");
    setErr("");
  };

  const submit = async () => {
    if (!active) return;
    try {
      await api.post(`/tasks/${active.id}/verify`, { action, remarks });
      setActive(null);
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="verifications-page">
      <header>
        <div className="label-small text-blue-700">Supervisor Queue</div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>
          Pending Verifications
        </h1>
        <p className="text-sm text-slate-600 mt-1">{tasks.length} task{tasks.length === 1 ? "" : "s"} awaiting your approval.</p>
      </header>

      {loading ? <div className="text-sm text-slate-500">Loading…</div> : (
        tasks.length === 0 ? (
          <div className="flat-card p-12 text-center">
            <ShieldCheck size={36} weight="duotone" className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">All caught up — nothing pending.</p>
          </div>
        ) : (
          <div className="flat-card">
            <table className="data-table">
              <thead>
                <tr><th>Area</th><th>Checklist</th><th>Submitted</th><th>Criticality</th><th></th></tr>
              </thead>
              <tbody>
                {tasks.map((t) => (
                  <tr key={t.id} data-testid={`verify-row-${t.id}`}>
                    <td className="font-semibold text-slate-900">{t.area_name}</td>
                    <td>{t.checklist_name}</td>
                    <td>{t.submitted_at ? new Date(t.submitted_at).toLocaleString() : "—"}</td>
                    <td><span className={`label-small crit-${t.area_criticality}`}>{t.area_criticality}</span></td>
                    <td className="text-right">
                      <button data-testid={`verify-open-${t.id}`} onClick={() => openTask(t)} className="px-3 py-1.5 text-xs font-semibold rounded-md border border-blue-200 text-blue-700 hover:bg-blue-50 transition-colors">Review</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Review modal */}
      {active && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4" onClick={() => setActive(null)}>
          <div className="bg-white border border-slate-200 rounded-md max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="verify-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="label-small text-blue-700">Verification Review</div>
                <h3 className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>{active.area?.name}</h3>
                <p className="text-sm text-slate-600">{active.checklist?.name}</p>
              </div>
              <span className={`status-pill status-${active.status}`}>{active.status.replace("_", " ")}</span>
            </div>

            <div className="divider mb-4" />

            <div className="space-y-3 mb-5">
              {(active.checklist?.questions || []).map((q) => {
                const r = (active.responses || []).find((x) => x.question_id === q.id);
                return (
                  <div key={q.id} className="border border-slate-200 rounded-md p-3">
                    <div className="text-sm text-slate-900 font-semibold">{q.text}</div>
                    <div className="text-sm text-slate-700 mt-1">
                      Answer: <span className="font-mono">{r?.answer || "—"}</span>
                      {r?.remarks ? <span className="text-slate-500 ml-3">• {r.remarks}</span> : null}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="space-y-3">
              <div className="flex gap-2">
                <button data-testid="action-approve" onClick={() => setAction("approve")} className={`flex-1 px-3 py-2 rounded-md text-sm font-bold uppercase tracking-wider border transition-colors ${action === "approve" ? "bg-green-600 text-white border-green-600" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}>
                  <CheckCircle size={14} weight="fill" className="inline mr-1.5" /> Approve
                </button>
                <button data-testid="action-reject" onClick={() => setAction("reject")} className={`flex-1 px-3 py-2 rounded-md text-sm font-bold uppercase tracking-wider border transition-colors ${action === "reject" ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}>
                  <XCircle size={14} weight="fill" className="inline mr-1.5" /> Reject
                </button>
              </div>

              <textarea data-testid="verify-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} placeholder="Verification remarks (optional, required for rejection)" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600" />

              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}

              <div className="flex gap-2 justify-end">
                <button onClick={() => setActive(null)} className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 rounded-md transition-colors">Cancel</button>
                <button data-testid="verify-submit" onClick={submit} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md transition-colors">Confirm {action}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
